/** Asset library: upload (button or drop anywhere), analysis progress, click or drag to place. */

import type { Asset } from "@studio/engine";
import { BookPlus, Brush, Circle, Mic, Hand, Image as ImageIcon, LibraryBig, Loader2, Minus, MoveRight, Music, Plus, Settings2, Square, TriangleAlert, Type, Upload } from "lucide-react";
import { useRef, useState } from "react";
import { api, fileUrl, type UploadResult } from "../api";
import { addClip, addImage, addShape, addText, removeAssets, unusedAssets } from "./actions";
import { placeholderMatches, queueAutoPlace } from "./autoplace";
import { useEditor } from "./store";

export async function uploadFiles(files: File[], kind?: "hand"): Promise<string[]> {
  const p = useEditor.getState().project;
  if (!p || files.length === 0) return [];
  return addAssets(await api.upload(p.id, files, kind));
}

/** Put newly stored assets (uploaded, or copied from the shared library) into the project. Returns the errors. */
export function addAssets(res: UploadResult[]): string[] {
  const added = res.filter((r) => r.asset).map((r) => r.asset!);
  if (added.length)
    useEditor.getState().edit((d) => {
      d.assets.push(...added);
    });
  // images named after scenes (S12.png …) replace their placeholders once analysed
  queueAutoPlace(added.map((a) => a.id));
  window.dispatchEvent(new Event("studio:poll"));
  return res.filter((r) => r.error).map((r) => `${r.name}: ${r.error}`);
}

export function Library() {
  const project = useEditor((s) => s.project)!;
  const analysis = useEditor((s) => s.analysis);
  const input = useRef<HTMLInputElement>(null);
  const handInput = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [note, setNote] = useState("");

  const upload = async (files: File[], kind?: "hand") => {
    setBusy(true);
    try {
      const before = new Set(useEditor.getState().project!.assets.map((a) => a.id));
      setErrors(await uploadFiles(files, kind));
      const added = useEditor.getState().project!.assets.find((a) => a.kind === "hand" && !before.has(a.id));
      if (kind === "hand" && added) useEditor.getState().set({ panel: { hand: added.id } });
    } catch (e) {
      setErrors([(e as Error).message]);
    } finally {
      setBusy(false);
    }
  };

  const images = project.assets.filter((a) => a.kind === "image" || a.kind === "svg");
  const audio = project.assets.filter((a) => a.kind === "audio");
  const hands = project.assets.filter((a) => a.kind === "hand");
  const fonts = project.assets.filter((a) => a.kind === "font");

  const share = async (a: Asset) => {
    setErrors([]);
    setNote("");
    try {
      const [r] = await api.toLibrary(project.id, [a.id]);
      if (r.error) setErrors([`${a.name}: ${r.error}`]);
      else setNote(r.duplicate ? `${a.name} is already in the shared library.` : `${a.name} added to the shared library.`);
    } catch (e) {
      setErrors([(e as Error).message]);
    }
  };
  const shareButton = (a: Asset, className = "tiny-icon icon") => (
    <button className={className} title="Copy to the shared library, for use in other projects"
      onClick={(e) => { e.stopPropagation(); void share(a); }}>
      <BookPlus size={13} />
    </button>
  );

  /** "voice", "music" or both, from the clips on the timeline that use this file */
  const roles = (id: string) => [...new Set(project.audio.filter((c) => c.assetId === id).map((c) => c.role))].sort().reverse().join(" + ");

  const unused = unusedAssets(project);
  const removeUnused = () => {
    const names = unused.map((a) => `• ${a.name}`).slice(0, 25).join("\n") + (unused.length > 25 ? `\n… and ${unused.length - 25} more` : "");
    if (!confirm(`Remove ${unused.length} file${unused.length === 1 ? "" : "s"} that nothing in the project uses?\n\n${names}\n\nUndo (Ctrl+Z) or History brings them back.`)) return;
    removeAssets(unused);
    setNote(`Removed ${unused.length} unused file${unused.length === 1 ? "" : "s"}.`);
    api.purgeAssets(project.id).catch(() => {});  // frees files older versions no longer need either
  };

  const place = (a: Asset) => {
    const size = analysis[a.id]?.size;
    addImage(a, size ? { w: size[0], h: size[1] } : null);
  };

  return (
    <div className="library">
      <div className="lib-actions">
        <button className="primary" onClick={() => input.current?.click()} disabled={busy}>
          {busy ? <Loader2 size={15} className="spin" /> : <Upload size={15} />} Upload
        </button>
        <input ref={input} type="file" multiple hidden accept=".png,.jpg,.jpeg,.webp,.svg,.m4a,.mp3,.wav,.aac,.ogg,.flac,.ttf,.otf"
          onChange={(e) => { void upload([...(e.target.files ?? [])]); e.target.value = ""; }} />
        <button onClick={() => useEditor.getState().set({ panel: "library" })} title="Images, audio and fonts shared by everyone">
          <LibraryBig size={15} /> Shared library
        </button>
      </div>
      <div className="lib-add">
        <button onClick={addText} title="Add text"><Type size={15} /> Text</button>
        <button onClick={() => addShape("rect")} title="Rectangle"><Square size={15} /></button>
        <button onClick={() => addShape("ellipse")} title="Ellipse"><Circle size={15} /></button>
        <button onClick={() => addShape("line")} title="Line"><Minus size={15} /></button>
        <button onClick={() => addShape("arrow")} title="Arrow"><MoveRight size={15} /></button>
      </div>
      {note && <div className="note" onClick={() => setNote("")}>{note}</div>}
      {errors.length > 0 && (
        <div className="errors" onClick={() => setErrors([])}>
          {errors.map((e) => <div key={e}><TriangleAlert size={13} /> {e}</div>)}
        </div>
      )}

      <h4><ImageIcon size={13} /> Images</h4>
      <FillPlaceholders />
      {images.length === 0 && <p className="muted small">Drop PNG, JPG, WebP or SVG files anywhere.</p>}
      <div className="thumbs">
        {images.map((a) => {
          const st = analysis[a.id];
          const job = st?.job;
          const running = job && (job.status === "queued" || job.status === "running");
          const failed = job?.status === "error";
          return (
            <div key={a.id} className="thumb" draggable onDragStart={(e) => e.dataTransfer.setData("application/x-studio-asset", a.id)}
              onClick={() => place(a)} title={`${a.name}\nClick to place at the playhead`}>
              <img src={fileUrl(project.id, `derived/${a.id}/thumb.png`)} alt="" />
              <span className="thumb-name">{a.name}</span>
              {running && (
                <span className="thumb-status">
                  <Loader2 size={12} className="spin" /> {job.status === "queued" ? "queued" : `${Math.round(job.progress * 100)}%`}
                </span>
              )}
              {failed && <span className="thumb-status bad" title={job.message}><TriangleAlert size={12} /> failed</span>}
              {shareButton(a, "lib-thumb-share")}
              <button className="thumb-gear" title="Outline detection & stroke order"
                onClick={(e) => { e.stopPropagation(); useEditor.getState().set({ panel: { analysis: a.id } }); }}>
                <Settings2 size={13} />
              </button>
            </div>
          );
        })}
      </div>

      <h4><Music size={13} /> Audio</h4>
      {audio.length === 0 && <p className="muted small">Voiceovers and music (M4A, MP3, WAV).</p>}
      <ul className="lib-list">
        {audio.map((a) => (
          <li key={a.id}>
            <span className="lib-audio-name" title={a.name}>{a.name}</span>
            <span className="muted">{a.duration ? `${a.duration.toFixed(1)}s` : ""}</span>
            {roles(a.id) && <span className="role-tag" title="How it's used on the timeline (change it in the clip's Role)">{roles(a.id)}</span>}
            <span className="row-actions">
              <button className="tiny" onClick={() => addClip(a, "voice")} title="Add at the playhead as a voiceover">+ voice</button>
              <button className="tiny" onClick={() => addClip(a, "music")} title="Add at the playhead as background music (ducks under voice)">+ music</button>
              <button className="tiny-icon icon" title="Change voice…" onClick={() => useEditor.getState().set({ panel: { voice: a.id } })}><Mic size={13} /></button>
              {shareButton(a)}
            </span>
          </li>
        ))}
      </ul>

      <h4><Hand size={13} /> Hands</h4>
      <ul className="lib-list">
        <li className="muted">Marker (built in)</li>
        {hands.map((a) => (
          <li key={a.id}>
            <button className="link" onClick={() => useEditor.getState().set({ panel: { hand: a.id } })} title="Set the pen tip and size">{a.name}</button>
          </li>
        ))}
      </ul>
      <button className="link small" onClick={() => handInput.current?.click()}><Plus size={12} /> Upload hand PNG</button>
      <input ref={handInput} type="file" hidden accept=".png,.webp" onChange={(e) => { void upload([...(e.target.files ?? [])], "hand"); e.target.value = ""; }} />

      <h4><Type size={13} /> Fonts</h4>
      <ul className="lib-list">
        {fonts.map((a) => (
          <li key={a.id}>
            {a.name} {analysis[a.id]?.ready === false && <Loader2 size={12} className="spin" />}
            {shareButton(a)}
          </li>
        ))}
      </ul>

      {unused.length > 0 && (
        <button className="link small lib-cleanup" onClick={removeUnused}
          title={`Not used by any layer, clip, hand or setting:\n${unused.map((a) => a.name).join("\n")}`}>
          <Brush size={12} /> Remove {unused.length} unused file{unused.length === 1 ? "" : "s"}
        </button>
      )}
    </div>
  );
}

/** Offers to fill import placeholders with already-uploaded images named after their scenes. */
function FillPlaceholders() {
  const project = useEditor((s) => s.project)!;
  const matches = placeholderMatches(project);
  if (matches.length === 0) return null;
  return (
    <button className="wide" title="Images named S01.png, S02.png … replace their scene's placeholder"
      onClick={() => queueAutoPlace(matches.map((m) => m.assetId))}>
      Place {matches.length} image{matches.length === 1 ? "" : "s"} into placeholders
    </button>
  );
}
